import { REST_URI } from 'lib/appConfig';
import { getAuthHeaders } from './helpers';
import TokenStore from './TokenStore';

let jwt = null;
let refreshInterval = null;
let inFlightFetch = null;

// Bumped by clear(). fetchJwt captures this before its request and discards the
// result on a mismatch
let sessionEpoch = 0;
const apiKey = new TokenStore('apikey');
const presetMachineToken = new TokenStore('presetMachine');

// The lifetime the api issues these tokens with.
const TOKEN_LIFETIME_MS = 300 * 1000;

// Headroom ahead of expiry. Used twice: the interval refetches this early, and
// getJwt treats a token this close to expiry as stale.
const EXPIRY_SKEW_MS = 30 * 1000;

const REFETCH_DURATION = TOKEN_LIFETIME_MS - EXPIRY_SKEW_MS;

// How long a failed mint suppresses the next one. The coalescer only dedupes
// concurrent callers, so without this every operation during a token endpoint
// outage costs its own request, fleet wide.
const MINT_BACKOFF_MS = 10 * 1000;

let mintBackoff = null;

const clearMintBackoff = () => {
  clearTimeout(mintBackoff);
  mintBackoff = null;
};

// Only the session that issued the request may open a backoff: a failure
// arriving from the session this one replaced must not suppress its mints.
const noteMintFailure = (epoch) => {
  if (epoch !== sessionEpoch || mintBackoff) {
    return;
  }

  mintBackoff = setTimeout(clearMintBackoff, MINT_BACKOFF_MS);
};

const parseJwt = (token) => {
  const base64Payload = token.split('.')[1];
  const payload = window.atob(base64Payload);
  return JSON.parse(payload.toString());
};

const fetchJwt = async () => {
  const url = `${REST_URI}/operator-view/user-token?graphql=1`;
  const key = apiKey.get();
  if (!key) {
    return null;
  }
  const epoch = sessionEpoch;
  // Installed before the request so a session whose first fetch rejects still
  // gets periodic attempts to recover.
  if (!refreshInterval) {
    refreshInterval = setInterval(() => {
      // Nothing awaits the timer's promise, so a rejecting fetch or a 200
      // carrying a non-JSON body would go unhandled every REFETCH_DURATION.
      return refreshJwt().catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[Auth error]: scheduled token refresh failed', err);

        return null;
      });
    }, REFETCH_DURATION);
  }

  let response;
  try {
    response = await fetch(url, { headers: getAuthHeaders(key) });
  } catch (err) {
    noteMintFailure(epoch);
    throw err;
  }

  if (!response.ok) {
    if (response.status === 401) {
      clear(true);
    }
    noteMintFailure(epoch);
    // Never cache a non-ok body. It is not a token, and getJwt now parses the
    // cached value on every call, so storing an error page here would move the
    // failure into parseJwt and break every later call instead of this one.
    return null;
  }

  const token = await response.json();
  if (epoch !== sessionEpoch) {
    // The session was cleared while this request was in flight. Caching now
    // would resurrect a token for a session that has already been torn down.
    return null;
  }
  jwt = token;
  clearMintBackoff();
  return jwt;
};

// Share one request between concurrent callers.
const refreshJwt = () => {
  if (!inFlightFetch) {
    const pending = fetchJwt().finally(() => {
      // Only retire our own request; clear() may already have replaced it.
      if (inFlightFetch === pending) {
        inFlightFetch = null;
      }
    });
    inFlightFetch = pending;
  }

  return inFlightFetch;
};

const getJwt = async () => {
  if (jwt) {
    try {
      // `exp` is a unix timestamp in seconds; Date.now() is milliseconds.
      const { exp } = parseJwt(jwt);

      if (exp * 1000 - EXPIRY_SKEW_MS > Date.now()) {
        return jwt;
      }
    } catch (err) {
      // An ok response can still carry something that is not a token.
      // eslint-disable-next-line no-console
      console.error('[Auth error]: discarding an unparseable token', err);
      jwt = null;
    }
  }

  // The interval keeps trying on its own cadence, so a caller declining here
  // defers to it rather than giving up on the session.
  if (mintBackoff) {
    return null;
  }

  return refreshJwt();
};

const clear = (force = false) => {
  const key = apiKey.get();
  if (key) {
    if (key.startsWith('at') || force) {
      apiKey.clear();
    }
  }

  jwt = null;

  // Release the coalescer and invalidate any request already in flight.
  inFlightFetch = null;
  sessionEpoch += 1;
  clearMintBackoff();

  // The timer starts its fetch after the epoch bump, so the guard in fetchJwt
  // does not cover it and it would mint a token for a torn down session.
  clearInterval(refreshInterval);
  refreshInterval = null;

  presetMachineToken.clear();
};

const getApiKey = () => {
  return apiKey.get();
};

const setApiKey = (value) => {
  return apiKey.set(value);
};

const clearJwt = () => {
  jwt = null;
};

export default {
  clear,
  getJwt,
  getApiKey,
  setApiKey,
  clearJwt,
};
